import type { MobileAcquisitionType, MobileDeviceOwner } from '@/shared/mobile-service/api/cors-mobile-api';

/**
 * Builders for the acquisitions list's `commonDeviceOwners` lookup.
 *
 * `commonDeviceOwners` is scoped to a single acquisition, so a page of the list
 * would otherwise cost one request per row. Aliasing collapses a chunk of ids
 * into one document — the same trick `build-facet-query.ts` uses for the facet
 * rail — turning a 100-row page into four round trips.
 *
 * Owners are read over GraphQL rather than the REST `/device-owners` route
 * because that route validates through `case_info` and 404s for acquisitions
 * with no case row, which is precisely the population this list now shows. The
 * GraphQL resolver validates against `acquisition_map` instead.
 *
 * Everything here is pure so the batching and merge rules stay testable without
 * a live schema.
 */

/**
 * Acquisitions per GraphQL document. Small enough that one unresolvable id
 * costs a handful of rows rather than the page: mobile-service reports errors
 * in the `errors` array, and the client treats any non-empty array as a failure
 * for the whole document.
 */
export const DEVICE_OWNER_BATCH_SIZE = 25;

/** Owner profiles requested per acquisition. Devices have a handful at most. */
export const DEVICE_OWNERS_PER_ACQUISITION = 20;

const OWNER_FIELDS = [
  'mobileArtifactId',
  'acquisitionType',
  'displayName',
  'llmOwner',
  'phoneNumbers',
  'emails',
  'confidenceTier',
].join(' ');

/** GraphQL aliases can't start with a digit, so ids are prefixed. */
function aliasFor(mobileAcquisitionId: number): string {
  return `a${mobileAcquisitionId}`;
}

function idForAlias(alias: string): number | null {
  if (!/^a\d+$/.test(alias)) return null;
  return Number(alias.slice(1));
}

export function chunkAcquisitionIds(
  ids: readonly number[],
  size: number = DEVICE_OWNER_BATCH_SIZE,
): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

/**
 * Build one aliased document per chunk of acquisition ids. Returns an empty
 * array when there is nothing to ask for, so callers can gate on length.
 */
export function buildDeviceOwnerBatchQueries(
  ids: readonly number[],
  size: number = DEVICE_OWNER_BATCH_SIZE,
): string[] {
  const unique = [...new Set(ids.filter((id) => Number.isInteger(id)))];
  if (unique.length === 0) return [];

  return chunkAcquisitionIds(unique, size).map((chunk) => {
    const selections = chunk.map(
      (id) =>
        `${aliasFor(id)}: commonDeviceOwners(mobileAcquisitionId: ${id}, offset: 0, limit: ${DEVICE_OWNERS_PER_ACQUISITION}) { items { ${OWNER_FIELDS} } }`,
    );
    return `query AcquisitionDeviceOwners {\n  ${selections.join('\n  ')}\n}`;
  });
}

interface OwnerConnection {
  items?: MobileDeviceOwner[] | null;
}

/** Reshape one aliased response back into owners keyed by acquisition id. */
export function parseDeviceOwnerBatch(
  data: Record<string, unknown> | null | undefined,
  into: Map<number, MobileDeviceOwner[]> = new Map(),
): Map<number, MobileDeviceOwner[]> {
  for (const [alias, value] of Object.entries(data ?? {})) {
    const id = idForAlias(alias);
    if (id == null) continue;
    const items = (value as OwnerConnection | null)?.items;
    into.set(id, Array.isArray(items) ? items : []);
  }
  return into;
}

/** The acquisition-list view of every owner profile on one device. */
export interface MergedDeviceOwner {
  /** Vendor, which the REST acquisition row doesn't carry. */
  acquisitionType: MobileAcquisitionType | null;
  /** Owner names, best-confidence first, joined for display. */
  owner: string | null;
  phoneNumbers: string[];
  emails: string[];
}

const EMPTY_MERGE: MergedDeviceOwner = {
  acquisitionType: null,
  owner: null,
  phoneNumbers: [],
  emails: [],
};

/** Lower tiers are better sources; an absent tier sorts last. */
function byConfidence(a: MobileDeviceOwner, b: MobileDeviceOwner): number {
  const unranked = Number.MAX_SAFE_INTEGER;
  return (a.confidenceTier ?? unranked) - (b.confidenceTier ?? unranked);
}

function dedupe(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

/**
 * Collapse every owner profile on a device into one row's worth of columns.
 *
 * A device with several OS-level profiles has several owner rows, and the
 * analyst wants the identifiers for the device, not for a profile — so phones
 * and emails are unioned across all of them. Names stay separate but ordered by
 * confidence, so the most authoritative one reads first. `llmOwner` is the
 * requested name, with `displayName` as the fallback for acquisitions the LLM
 * pass hasn't reached.
 */
export function mergeDeviceOwners(
  owners: readonly MobileDeviceOwner[] | undefined,
): MergedDeviceOwner {
  if (!owners || owners.length === 0) return EMPTY_MERGE;

  const ordered = [...owners].sort(byConfidence);
  return {
    acquisitionType: ordered.find((o) => o.acquisitionType)?.acquisitionType ?? null,
    owner: dedupe(ordered.map((o) => o.llmOwner ?? o.displayName)).join(', ') || null,
    phoneNumbers: dedupe(ordered.flatMap((o) => o.phoneNumbers ?? [])),
    emails: dedupe(ordered.flatMap((o) => o.emails ?? [])),
  };
}
